/**
 * collectibleStep.ts
 * Per-tick collectible pickup detection for Starfall Courier.
 *
 * Checks Nova's hitbox against uncollected Star Seeds and Constellation Shards.
 * On overlap, marks the collectible as collected and updates gameState counters.
 *
 * No React imports. No async calls. Pure engine logic.
 *
 * @module collectibleStep
 */

import { gameState, type CollectibleRuntime } from './gameState';
import { NOVA_WIDTH, NOVA_HEIGHT, TILE_SIZE, SEED_SCORE, SHARD_SCORE, HEART_FRAGMENT_SCORE } from '../../constants/gameConstants';
import { maxHealthForFragments } from '../data/heartUpgrades';
import { dispatch } from './gameEventDispatch';

/** Hitbox size for collectibles (same as tile size). */
const COLLECTIBLE_SIZE = TILE_SIZE;

/**
 * Check Nova against all uncollected collectibles this tick.
 * Mutates gameState.collectibles (sets collected=true), gameState.seeds,
 * and gameState.score.
 */
export function stepCollectibles(): void {
  const { nova, collectibles } = gameState;
  const novaRight = nova.x + NOVA_WIDTH;
  const novaBottom = nova.y + NOVA_HEIGHT;

  for (let i = 0; i < collectibles.length; i++) {
    const c: CollectibleRuntime = collectibles[i];
    if (c.collected) continue;

    // AABB overlap check (inline for performance — hot path)
    const cRight = c.x + COLLECTIBLE_SIZE;
    const cBottom = c.y + COLLECTIBLE_SIZE;
    if (nova.x >= cRight || novaRight <= c.x || nova.y >= cBottom || novaBottom <= c.y) {
      continue;
    }

    // DECISION: Mark collected inline rather than splicing the array.
    // Array mutations in the hot loop would cause O(n²) shifts; a boolean
    // flag is O(1) and the renderer simply skips collected items.
    c.collected = true;

    if (c.kind === 'seed') {
      gameState.seeds += 1;
      gameState.score += SEED_SCORE;
      dispatch.seedCollected();
    } else if (c.kind === 'shard') {
      // Constellation Shard
      gameState.shardsCollected += 1;
      gameState.score += SHARD_SCORE;
      dispatch.shardCollected();
    } else if (c.kind === 'heart_fragment') {
      // DECISION: Heart Fragments are permanent progression. `heartFragments`
      // holds the running total (persisted fragments plus this session's), and
      // maxHealth is recomputed from it rather than incremented, so a whole
      // heart only lands on the second fragment of each pair. See
      // `heartUpgrades.ts` for why fractional max HP is not used.
      gameState.heartFragments += 1;
      const upgraded = maxHealthForFragments(gameState.heartFragments);
      if (upgraded > gameState.nova.maxHealth) {
        // A new container arrives filled — the pickup should read as a reward,
        // not as an empty slot the player still has to find a heal for.
        gameState.nova.health += upgraded - gameState.nova.maxHealth;
        gameState.nova.maxHealth = upgraded;
      }
      gameState.score += HEART_FRAGMENT_SCORE;
      dispatch.heartFragmentCollected();
    }
  }
}
